import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  AlertTriangle, Users, Brain, RefreshCw, Heart, 
  TrendingUp, Shield, CheckCircle2, XCircle, 
  Flame, Sparkles, Activity, Calendar, Loader2,
  ThermometerSun, BatteryWarning, HeartPulse, Clock,
  Zap, ArrowRight
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, RadialBarChart, RadialBar } from 'recharts';
import ReactMarkdown from 'react-markdown';
import { ExportDropdown } from '@/components/ExportDropdown';
import { InsightsExport } from '@/components/InsightsExport';
import { 
  PageHeader, MetricCard, StatusBadge, TeamBadge, DisciplineBadge, 
  RiskGauge, GradientProgress, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = {
  CRITICAL: { bg: 'bg-gradient-to-r from-rose-500 to-red-600', text: 'text-red-600', light: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800' },
  HIGH: { bg: 'bg-gradient-to-r from-orange-500 to-amber-600', text: 'text-orange-600', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800' },
  MODERATE: { bg: 'bg-gradient-to-r from-amber-400 to-yellow-500', text: 'text-amber-600', light: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800' },
  LOW: { bg: 'bg-gradient-to-r from-emerald-500 to-green-600', text: 'text-emerald-600', light: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800' }
};

const getRiskConfig = (level) => {
  return RISK_COLORS[level] || RISK_COLORS['LOW'];
};

export default function BurnoutDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedData, setFeedData] = useState(null);
  const [legacyData, setLegacyData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // RN Capacity period selection for data consistency
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [rncmPeriods, setRncmPeriods] = useState([]);

  // Fetch available RN Capacity periods
  const fetchRncmPeriods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/capacity-projection/rncm-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRncmPeriods(data.periods || []);
        // Auto-select first period if available
        if (data.periods?.length > 0 && !selectedPeriod) {
          setSelectedPeriod(data.periods[0].period_start);
        }
      }
    } catch (error) {
      console.error('Error fetching RN Capacity periods:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Build period query parameter
      const periodParam = selectedPeriod ? `?period_start=${selectedPeriod}` : '';
      
      // Fetch from the new integrated feed endpoint
      const [feedRes, legacyRes] = await Promise.all([
        fetch(`${API_URL}/api/capacity-projection/feed/burnout-risk${periodParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/burnout/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);

      if (feedRes.ok) {
        const data = await feedRes.json();
        setFeedData(data);
      }

      if (legacyRes?.ok) {
        const data = await legacyRes.json();
        setLegacyData(data);
      }
    } catch (error) {
      console.error('Error loading burnout data:', error);
      toast.error('Error loading dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRncmPeriods();
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [token, selectedPeriod]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Dashboard refreshed');
  };

  const handleGenerateAnalysis = async () => {
    setGeneratingAnalysis(true);
    try {
      const response = await fetch(`${API_URL}/api/burnout/ai-analysis`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAiAnalysis(data.analysis);
        toast.success('AI analysis generated');
      } else {
        toast.error('Failed to generate analysis');
      }
    } catch (error) {
      toast.error('Error generating analysis');
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Use integrated feed data or fall back to legacy data
  const summary = feedData?.summary || legacyData?.summary || {};
  const clinicians = feedData?.clinicians || [];
  const legacyClinicians = legacyData?.clinicians || [];
  const teamBreakdown = legacyData?.team_breakdown || {};

  // Group clinicians by risk level
  const criticalRisk = clinicians.filter(c => c.risk_level === 'CRITICAL');
  const highRisk = clinicians.filter(c => c.risk_level === 'HIGH');
  const moderateRisk = clinicians.filter(c => c.risk_level === 'MODERATE');
  const lowRisk = clinicians.filter(c => c.risk_level === 'LOW');

  // Prepare chart data
  const riskDistribution = [
    { name: 'Critical', value: summary.critical_count || criticalRisk.length, fill: '#EF4444' },
    { name: 'High', value: summary.high_count || highRisk.length, fill: '#F97316' },
    { name: 'Moderate', value: summary.moderate_count || moderateRisk.length, fill: '#F59E0B' },
    { name: 'Low', value: summary.low_count || lowRisk.length, fill: '#10B981' }
  ].filter(d => d.value > 0);

  // Group by team from integrated data
  const teamData = {};
  clinicians.forEach(c => {
    const team = c.team || 'Unknown';
    if (!teamData[team]) {
      teamData[team] = { total: 0, critical: 0, high: 0, moderate: 0, low: 0, avgScore: 0, totalScore: 0 };
    }
    teamData[team].total++;
    teamData[team].totalScore += c.risk_score || 0;
    teamData[team].avgScore = Math.round(teamData[team].totalScore / teamData[team].total);
    if (c.risk_level === 'CRITICAL') teamData[team].critical++;
    else if (c.risk_level === 'HIGH') teamData[team].high++;
    else if (c.risk_level === 'MODERATE') teamData[team].moderate++;
    else teamData[team].low++;
  });

  const teamChartData = Object.entries(teamData).map(([team, data]) => ({
    team,
    Critical: data.critical,
    High: data.high,
    Moderate: data.moderate,
    Low: data.low,
    avgScore: data.avgScore
  }));

  return (
    <div className="space-y-6" data-testid="burnout-dashboard">
      {/* Modern Gradient Header */}
      <PageHeader
        icon={HeartPulse}
        title="Burnout Risk Intelligence"
        subtitle="AI-powered workforce wellness monitoring from Capacity Projection"
        gradient="from-rose-600 via-red-600 to-orange-600"
      >
        <Button 
          variant="secondary"
          className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button 
          className="bg-white text-rose-600 hover:bg-white/90"
          onClick={handleGenerateAnalysis}
          disabled={generatingAnalysis}
        >
          {generatingAnalysis ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          AI Analysis
        </Button>
      </PageHeader>

      {/* Period Selector and Data Source Indicator */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-600" />
                <span className="font-medium text-slate-700">RN Capacity Period:</span>
              </div>
              {rncmPeriods.length > 0 ? (
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="burnout-period-selector"
                >
                  {rncmPeriods.map((period) => (
                    <option key={period.period_start} value={period.period_start}>
                      {period.period_start} → {period.period_end} ({period.clinician_count} clinicians)
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-muted-foreground">No periods available</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Zap className="h-4 w-4 text-emerald-500" />
              <span>Source: {feedData?.source === 'rncm_capacity' ? 'RN Capacity' : 'Projection Data'}</span>
              <span className="text-xs">• {feedData?.timestamp ? new Date(feedData.timestamp).toLocaleTimeString() : 'N/A'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Staff"
          value={summary.total_staff || clinicians.length}
          icon={Users}
          gradient="indigo"
          subtitle="Clinicians monitored"
        />
        <MetricCard
          title="Critical Risk"
          value={summary.critical_count || criticalRisk.length}
          icon={Flame}
          gradient="rose"
          subtitle="Immediate intervention"
        />
        <MetricCard
          title="High Risk"
          value={summary.high_count || highRisk.length}
          icon={AlertTriangle}
          gradient="amber"
          subtitle="Close monitoring"
        />
        <MetricCard
          title="Moderate Risk"
          value={summary.moderate_count || moderateRisk.length}
          icon={BatteryWarning}
          gradient="blue"
          subtitle="Preventive action"
        />
        <MetricCard
          title="Low Risk"
          value={summary.low_count || lowRisk.length}
          icon={Heart}
          gradient="emerald"
          subtitle="Healthy status"
        />
      </div>

      {/* Critical Alerts Banner */}
      {criticalRisk.length > 0 && (
        <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-500 rounded-xl">
                <Flame className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800 dark:text-red-200">Critical Burnout Alert</h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {criticalRisk.length} staff member(s) at critical burnout risk requiring immediate intervention:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {criticalRisk.slice(0, 5).map((c, idx) => (
                    <Badge key={idx} className="bg-red-500 text-white">
                      {c.clinician_name} ({c.risk_score}%)
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="overview" className={modernTabTriggerClass}>
            <Activity className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="clinicians" className={modernTabTriggerClass}>
            <Users className="h-4 w-4 mr-2" /> Staff Details
          </TabsTrigger>
          <TabsTrigger value="teams" className={modernTabTriggerClass}>
            <Shield className="h-4 w-4 mr-2" /> By Team
          </TabsTrigger>
          <TabsTrigger value="analysis" className={modernTabTriggerClass}>
            <Brain className="h-4 w-4 mr-2" /> AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Risk Distribution Chart */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Activity} title="Risk Distribution" />
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-4 text-xs">
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-500" /> Critical</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-orange-500" /> High</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-amber-500" /> Moderate</div>
                  <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-emerald-500" /> Low</div>
                </div>
              </CardContent>
            </ModernCard>

            {/* High Risk Staff */}
            <ModernCard className="border-orange-200 dark:border-orange-800">
              <CardHeader>
                <SectionHeader icon={AlertTriangle} title="High Risk Staff" subtitle="Requiring attention" />
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  {[...criticalRisk, ...highRisk].length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-2" />
                      <p>No high-risk staff</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {[...criticalRisk, ...highRisk].slice(0, 6).map((c, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg ${getRiskConfig(c.risk_level).light} border ${getRiskConfig(c.risk_level).border}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{c.clinician_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <TeamBadge team={c.team} />
                                <DisciplineBadge discipline={c.discipline} />
                              </div>
                            </div>
                            <RiskGauge score={c.risk_score} size="sm" />
                          </div>
                          {c.contributing_factors?.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              {c.contributing_factors.slice(0, 2).map((f, i) => (
                                <span key={i}>• {f} </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </ModernCard>

            {/* Quick Actions */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Zap} title="Quick Actions" subtitle="Recommended interventions" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {criticalRisk.length > 0 && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border-l-4 border-red-500">
                      <p className="font-medium text-sm text-red-800 dark:text-red-200">P0 - Immediate</p>
                      <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                        Schedule 1:1 with {criticalRisk[0]?.clinician_name}
                      </p>
                    </div>
                  )}
                  {highRisk.length > 0 && (
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border-l-4 border-orange-500">
                      <p className="font-medium text-sm text-orange-800 dark:text-orange-200">P1 - This Week</p>
                      <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                        Review workload for {highRisk.length} high-risk staff
                      </p>
                    </div>
                  )}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                    <p className="font-medium text-sm text-blue-800 dark:text-blue-200">P2 - Ongoing</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Monitor team utilization trends weekly
                    </p>
                  </div>
                </div>
              </CardContent>
            </ModernCard>
          </div>

          {/* Team Risk Overview */}
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={Shield} title="Team Risk Analysis" />
            </CardHeader>
            <CardContent>
              {teamChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={teamChartData}>
                    <XAxis dataKey="team" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Critical" stackId="a" fill="#EF4444" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="High" stackId="a" fill="#F97316" />
                    <Bar dataKey="Moderate" stackId="a" fill="#F59E0B" />
                    <Bar dataKey="Low" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No team data available</p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Staff Details Tab */}
        <TabsContent value="clinicians">
          <ModernCard>
            <CardHeader>
              <CardTitle>Complete Staff Burnout Assessment</CardTitle>
              <CardDescription>Individual risk scores from Capacity Projection data</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    <TableHead>Clinician</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Discipline</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Risk Score</TableHead>
                    <TableHead>Utilization</TableHead>
                    <TableHead>Contributing Factors</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clinicians.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No clinician data available. Add capacity data to see burnout risk.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clinicians.map((c, idx) => (
                      <TableRow 
                        key={idx} 
                        data-testid={`clinician-row-${idx}`}
                        className={c.risk_level === 'CRITICAL' ? 'bg-red-50/50 dark:bg-red-900/10' : c.risk_level === 'HIGH' ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}
                      >
                        <TableCell className="font-medium">{c.clinician_name}</TableCell>
                        <TableCell><TeamBadge team={c.team} /></TableCell>
                        <TableCell><DisciplineBadge discipline={c.discipline} /></TableCell>
                        <TableCell>
                          <StatusBadge status={c.risk_level} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${getRiskConfig(c.risk_level).text}`}>
                              {c.risk_score}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <GradientProgress value={c.utilization} max={100} />
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 max-w-[200px]">
                            {(c.contributing_factors || []).slice(0, 2).map((f, i) => (
                              <Badge key={i} variant="outline" className="text-xs mr-1">
                                {f}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-xs text-muted-foreground max-w-[200px]">
                            {c.recommendation}
                          </p>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(teamData).map(([team, data]) => (
              <ModernCard key={team} highlight={data.critical > 0 || data.high > 0}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <TeamBadge team={team} />
                    <Badge variant="outline">{data.total} staff</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center my-4">
                    <RiskGauge score={data.avgScore} size="md" />
                  </div>
                  <div className="text-center text-sm text-muted-foreground mb-4">
                    Average Risk Score
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div>
                      <div className="text-red-500 font-bold text-lg">{data.critical}</div>
                      <div className="text-muted-foreground">Critical</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-bold text-lg">{data.high}</div>
                      <div className="text-muted-foreground">High</div>
                    </div>
                    <div>
                      <div className="text-amber-500 font-bold text-lg">{data.moderate}</div>
                      <div className="text-muted-foreground">Moderate</div>
                    </div>
                    <div>
                      <div className="text-emerald-500 font-bold text-lg">{data.low}</div>
                      <div className="text-muted-foreground">Low</div>
                    </div>
                  </div>
                </CardContent>
              </ModernCard>
            ))}
          </div>
        </TabsContent>

        {/* AI Analysis Tab */}
        <TabsContent value="analysis">
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader 
                  icon={Brain} 
                  title="AI-Powered Burnout Analysis" 
                  subtitle="Comprehensive analysis with intervention recommendations"
                />
                <div className="flex items-center gap-2">
                  {aiAnalysis && (
                    <InsightsExport 
                      insights={[{
                        title: "Burnout Risk Analysis",
                        description: aiAnalysis,
                        severity: criticalRisk.length > 0 ? "critical" : highRisk.length > 0 ? "warning" : "info",
                        category: "Burnout Prevention",
                        recommendation: criticalRisk.length > 0 
                          ? `Immediate intervention needed for ${criticalRisk.length} staff member(s)` 
                          : "Continue monitoring workload trends"
                      }]}
                      pageTitle="Burnout Analysis"
                      data-testid="export-burnout-analysis"
                    />
                  )}
                  <Button 
                    onClick={handleGenerateAnalysis}
                    disabled={generatingAnalysis}
                    className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                  >
                    {generatingAnalysis ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Generate Analysis
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {aiAnalysis ? (
                <ScrollArea className="h-[400px] rounded-lg border p-4 bg-muted/30">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{aiAnalysis}</ReactMarkdown>
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Click "Generate Analysis" to get AI-powered insights</p>
                  <p className="text-sm mt-2">
                    The AI will analyze workload patterns and provide personalized recommendations
                  </p>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
